import { ApiService } from "utils/ApiService";
import { ADMIN_TYPE, API_URL } from "utils/Constants";
import { NODE_TYPE_COLUMN_MAP } from "./constants";
import { PRICING_CONSTANTS, PRODUCT_HEADERS } from "../../config/constants";
import { INITIAL_PRICING_TEMPLATE } from "./pricingTemplate";

const NODE_TYPE_LEVELS = {
  DEFAULT: 1,
  REGION: 1,
  PRODUCT: 2,
  CARD_TYPE: 3,
  SCHEME: 4,
  TRANSACTION_TYPE: 5,
};

let cachedRawResponseStructure = null;

export const validateRate = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return PRICING_CONSTANTS.ERROR_REQUIRED;
  }
  
  const cleanValue = String(value).trim();

  // Test 1: Is it even a valid base numeric format? (Allows digits and a single decimal point)
  if (!/^\d+(\.\d+)?$/.test(cleanValue)) {
    return PRICING_CONSTANTS.ERROR_NUMERIC; 
  }

  // Test 2: Enforce max 2 decimal places and a range of 0 to 100
  const strictFinancialRegex = /^(100(\.0{1,2})?|[0-9]{1,2}(\.[0-9]{1,2})?)$/;
  
  if (!strictFinancialRegex.test(cleanValue)) {
    return PRICING_CONSTANTS.ERROR_RANGE;
  }
  
  return "";
};

export const toDisplayRate = (value, source) => {
  if (value === null || value === undefined || value === "") return "";
  return source === "DEFAULT" ? `${value} (default)` : `${value}`;
};

export const getNodeOriginalValue = (node, field) => {
  const value = node?.rate?.[field];
  if (value === null || value === undefined) return "";
  return `${value}`;
};

export const getNodeColumnKey = (nodeType) => {
  return NODE_TYPE_COLUMN_MAP[nodeType] || "";
};

const flattenNewTree = (node, parentId = null, counter = { value: 0 }) => {
  if (!node) return [];

  counter.value += 1;
  const labelSlug = node.label
    ? node.label.toLowerCase().replace(/\s+/g, "-")
    : "node";
  const currentNodeId =
    node.id || `${node.type.toLowerCase()}-${labelSlug}-${counter.value}`;
  node.id = currentNodeId;

  const visualLevel = NODE_TYPE_LEVELS[node.type] || 1;
  const hasMdr = !!node.mdr;

  const normalizedNode = {
    id: currentNodeId,
    parentId,
    level: visualLevel,
    nodeType: node.type,
    label: node.label || "",
    rate: {
      mdrPercent:
        hasMdr && node.mdr.percent !== undefined ? `${node.mdr.percent}` : "",
      mdrFlat: hasMdr && node.mdr.flat !== undefined ? `${node.mdr.flat}` : "",
      source: hasMdr ? "NODE" : "DEFAULT",
    },
    permissions: node.permissions || {
      isEditable: true,
      canEditMdrPercent: true,
      canEditMdrFlat: true,
    },
  };

  let flatList = [normalizedNode];

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      flatList = flatList.concat(flattenNewTree(child, currentNodeId, counter));
    });
  }

  return flatList;
};

const normalizePricingResponse = (response) => {
  // Catch authentication errors early before parsing data payloads
  if (
    (response?.error !== undefined && response?.error?.status_code === 401) ||
    response?.status === "403"
  ) {
    throw new Error("SESSION_EXPIRED");
  }

  // 1. Handle common API wrapper utilities securely
  const body = response?.body ?? response?.data ?? response;
  
  let rootNode = null;

  // 2. Smart Detection: Check if data is wrapped in '.root', or if the body itself IS the tree
  if (body?.root) {
    rootNode = body.root;
  } else if (body && (body.type || body.children || body.label)) {
    // The backend returned the tree directly at the top level of the object!
    rootNode = body;
  }

  // 3. Fallback to template ONLY if the backend object is completely empty ({})
  if (!rootNode || Object.keys(rootNode).length === 0) {
    console.log("Fallback Intercept: Backend is empty. Injecting zero-value template.");
    rootNode = JSON.parse(JSON.stringify(INITIAL_PRICING_TEMPLATE.root));
  } else {
    console.log("Success: Loaded live updated tree data directly from backend object structure.");
  }

  // Preserve absolute separation of references to prevent memory mutations
  cachedRawResponseStructure = {
    merchantId: body?.merchantId || rootNode?.merchantId || getMerchantIdByRole(),
    pendingCount: body?.pendingCount ?? response?.pendingCount ?? 0,
    root: JSON.parse(JSON.stringify(rootNode)),
  };

  const counter = { value: 0 };
  return {
    pendingCount: cachedRawResponseStructure.pendingCount,
    tree: flattenNewTree(cachedRawResponseStructure.root, null, counter),
  };
};

const getMerchantIdByRole = () => {
  const role = localStorage.getItem("role");
  if (role === ADMIN_TYPE.SUPERADMIN) {
    return localStorage.getItem("globalMerchantId") || "";
  }
  if (role === ADMIN_TYPE.SCHOOLADMIN) {
    return localStorage.getItem("schoolAdminMerchantId") || "";
  }
  return "";
};

const unflattenTree = (currentNode, dirtyValues) => {
  if (!currentNode) return null;

  const patch = dirtyValues[currentNode.id];
  if (patch) {
    if (!currentNode.mdr) currentNode.mdr = {};
    if (patch.mdrPercent !== undefined)
      currentNode.mdr.percent = Number(patch.mdrPercent);
    if (patch.mdrFlat !== undefined)
      currentNode.mdr.flat = Number(patch.mdrFlat);
  }

  if (
    currentNode.id &&
    typeof currentNode.id === "string" &&
    currentNode.id.includes("-")
  ) {
    delete currentNode.id;
  }

  if (Array.isArray(currentNode.children)) {
    currentNode.children.forEach((child) => unflattenTree(child, dirtyValues));
  }

  return currentNode;
};

// Updated to build a strict path-parameter route mapping: /api/pricing/<merchantId>
export const fetchPricingTree = async () => {
  if (!API_URL?.PRICING) {
    throw new Error("Missing network endpoints configuration resource");
  }

  const merchantId = getMerchantIdByRole();
  
  // Guard clause: If no merchant context is loaded yet, bypass network call to avoid a broken 404 route
  if (!merchantId) {
    console.warn("No valid merchantId detected. Serving initial structure configuration locally.");
    return normalizePricingResponse({ root: null });
  }

  try {
    const requestUrl = `${API_URL.PRICING}/${merchantId}`;
    const response = await ApiService.getRequest(requestUrl, PRODUCT_HEADERS);
    return normalizePricingResponse(response);
  } catch (error) {
    if (error?.message === "SESSION_EXPIRED") {
      throw error;
    }
    console.warn(
      "Pricing engine API fallback intercept triggered. Injecting clean template model.",
      error
    );
    return normalizePricingResponse({ root: null });
  }
};

// Keeps unchanged - posts the full state payload cleanly to base URL path via POST
export const submitPricingChanges = async (dirtyValues) => {
  if (!API_URL?.PRICING) {
    throw new Error("Missing writeback routing engine definition setup");
  }

  if (!cachedRawResponseStructure) {
    throw new Error(
      "Cannot serialize modifications without a baseline structural state"
    );
  }

  const baseTreeStructure = cachedRawResponseStructure;
  
  const completelyMappedRoot = unflattenTree(
    JSON.parse(JSON.stringify(baseTreeStructure.root)),
    dirtyValues
  );

  const payload = {
    merchantId: baseTreeStructure.merchantId || getMerchantIdByRole(),
    root: completelyMappedRoot,
  };

  const response = await ApiService.postRequest(API_URL.PRICING, payload, PRODUCT_HEADERS);

  if (
    (response?.error !== undefined && response?.error?.status_code === 401) ||
    response?.status === "403"
  ) {
    throw new Error("SESSION_EXPIRED");
  }

  return response;
};

export const clearCachedStructure = () => {
  cachedRawResponseStructure = null;
};